import {
  ICurrencyList,
  IJobLocations,
  ISalaryRange,
} from "../types/campaign.types";
import { formatNumber } from "./helpers";

export const currencyList: ICurrencyList[] = [
  {
    id: "INR",
    name: "Indian Rupees",
    sign: "₹",
    code: "in",
  },
  {
    id: "BHD",
    name: "Bahraini Dinars",
    sign: ".د.ب",
    code: "bh",
  },
  {
    id: "AED",
    name: "Dirhams",
    sign: "د.إ",
    code: "ae",
  },
  {
    id: "HKD",
    name: "Hong Kong Dollar",
    sign: "$",
    code: "hk",
  },
  {
    id: "IDR",
    name: "Indonesian Rupiah",
    sign: "Rp",
    code: "id",
  },
  {
    id: "JOD",
    name: "Jordanian Dinars",
    sign: "JD",
    code: "jo",
  },
  {
    id: "KWD",
    name: "Kuwaiti Dinars",
    sign: "د.ك",
    code: "kw",
  },
  {
    id: "LBP",
    name: "Lebanese Pounds",
    sign: "LL",
    code: "lb",
  },
  {
    id: "OMR",
    name: "Omani Riyals",
    sign: "ر.ع.",
    code: "om",
  },
  {
    id: "MOP",
    name: "Pataca",
    sign: "MOP$",
    code: "mo",
  },
  {
    id: "PHP",
    name: "Philippine Peso",
    sign: "₱",
    code: "ph",
  },
  {
    id: "QAR",
    name: "Qatari Riyals",
    sign: "ر.ق",
    code: "qa",
  },
  {
    id: "MYR",
    name: "Malaysian Ringgit",
    sign: "RM",
    code: "my",
  },
  {
    id: "SAR",
    name: "Saudi Riyals",
    sign: "ر.س",
    code: "sa",
  },
  {
    id: "SGD",
    name: "Singapore Dollar",
    sign: "$",
    code: "sg",
  },
  {
    id: "TWD",
    name: "Taiwan Dollar",
    sign: "$",
    code: "tw",
  },
  {
    id: "THB",
    name: "Thai Baht",
    sign: "฿",
    code: "th",
  },
  {
    id: "USD",
    name: "US Dollar",
    sign: "$",
    code: "us",
  },
  {
    id: "VND",
    name: "Vietnamese Dong",
    sign: "₫",
    code: "vn",
  },
  {
    id: "CNY",
    name: "Yuan Renminbi",
    sign: "¥",
    code: "cn",
  },
];

export const getErrorMsg = ({
  error,
  defaultMsg,
}: {
  error: any;
  defaultMsg?: string;
}) => {
  return (
    error?.data?.message ||
    defaultMsg ||
    "Something went wrong. Please try again."
  );
};

export const MAX_COUNT = 5000;

export const getIsTriggeredFromFolder = (triggeredFromPage: string) => {
  return ["folder", "fm"]?.includes(triggeredFromPage?.toLowerCase());
};

export const getRandomValue = () => {
  return Math.floor(Math.random() * 11000);
};

export const locationsArrayToStr = (data: IJobLocations[]) => {
  return `${data?.map((item) => {
    if (item?.name?.includes(",")) {
      const parts = item?.name.split(",");
      if (parts.length > 1) {
        const firstPart = parts[0].trim();
        const remainingParts = parts.slice(1).join(", ").trim();
        return ` ${firstPart} (${remainingParts})`;
      }
    }
    return ` ${item?.name}`;
  })}`;
};

export const isSeaAndGulf = (siteContext?: string) => {
  if (!siteContext) {
    return false;
  }

  return siteContext !== "rexmonster";
};

export const unFormatNumberString = (value: string, sign?: string) => {
  return value?.replace(new RegExp(`[${sign},+]`, "g"), "")?.trim() || "";
};

export const formatNumberString = ({
  numStr,
  sign,
  isIndianCurrency,
  isMin
}: {
  numStr: string;
  sign?: string;
  isIndianCurrency: boolean;
  isMin?:boolean;
}): string => {
  let formattedValue = (numStr.match(/\d+(\.\d+)?/g) || []).join(""); // extracting only numbers

  const locale = isIndianCurrency ? "en-In" : "en-US";
  const isOneCr = formattedValue === "10000000";

  if (formattedValue) {
    // allow max 1 cr for indian currency
    // allow max 9,999,999 (7 digits) for non-indian currency

    if (isIndianCurrency && +formattedValue > 10000000) {
      formattedValue = new Intl.NumberFormat(locale).format(
        Number(formattedValue?.slice(0, -1))
      );
    } else if (!isIndianCurrency && +formattedValue >= 10000000) {
      formattedValue = new Intl.NumberFormat(locale).format(9999999);
    } else {
      formattedValue = new Intl.NumberFormat(locale).format(
        Number(formattedValue)
      );
    }
  }

  if (isIndianCurrency && numStr?.includes("+") && isOneCr && !isMin) {
    // adding + for 1cr+ salary
    formattedValue += "+";
  }

  return `${formattedValue && sign ? sign : ""} ${formattedValue}`.trim();
};

export const generateSalaryRange = (payload?: {
  startValue?: number;
  endValue?: number;
  isIndianCurrency?: boolean;
}) => {
  const start = payload?.startValue || 0;
  const end = payload?.endValue || 10000000;
  const range = [];

  let i = start;
  while (i <= end) {
    const val = formatNumberString({
      numStr: i.toString(),
      isIndianCurrency: !!payload?.isIndianCurrency,
    });
    range.push({
      label: val,
      value: val,
    });

    if (i < 1000000) {
      i += 50000; // increment by 50,000 up to 10,00,000
    } else if (i < 5000000) {
      i += 100000; // increment by 1,00,000 from 10,00,000 to 50,00,000
    } else {
      i += 500000; // increment by 5,00,000 from 50,00,000 to 1,00,00,000
    }
  }

  return range;
};

export const convertFormattedValueToInteger = (
  value?: string | null
): number | null => {
  if (!value) {
    return null;
  }

  // Remove commas and parse the string to an integer
  const unformattedValue = value.replace(/,/g, "").trim();

  const parsedInteger = parseInt(unformattedValue, 10);

  return isNaN(parsedInteger) ? null : parsedInteger;
};

export const getFormattedSalary = (
  salaryRange: ISalaryRange,
  formatValues?: boolean
): string => {
  if (!salaryRange?.min || !salaryRange?.max) return "";

  if (formatValues) {
    const format = salaryRange.currency === "INR" ? "indian" : "international";
    return `${formatNumber(+salaryRange.min, format)} to ${formatNumber(
      +salaryRange.max,
      format
    )} (Annual)`;
  }

  return `${salaryRange.min} - ${salaryRange.max}`;
};

// Regular expression to check only numbers
export const numberCheckRegex = /^[0-9]*$/;

export const getIsFresher = (exp: string) => {
  return exp?.toLowerCase() === "fresher";
};
